/* =============================================================================
   assets/sg-cart-summary.js

   Seconda Golf — Cart summary (v1.0, 30 Sep 2026)

   Keeps the Purchase summary card on /cart current as Dawn's cart script
   changes quantities or removes lines without a reload, and sends the
   customer to Shopify's checkout once the terms are ticked.

   It replaces assets/sg-cart-selling.js. Everything to do with the selling
   basket is gone: local storage, the payment toggle, the box fee, the
   Selling summary, the sell-checkout route and the header badge event.
   Selling no longer goes through the basket.

   WHAT WAS KEPT, unchanged in behaviour:

   1. render() IS SYNCHRONOUS. It must stay that way. If something in here
      ever needs to await a fetch, put the fetch in refreshCart() and let it
      re-render afterwards. This is what stops the blank-page flash.

   2. THE CART FIGURES ARE SEEDED BY LIQUID, through "cart" in the config
      block, in rappen. The first render needs no network.

   3. refreshCart() fetches /cart.js and re-renders. On failure it keeps the
      seeded figures rather than zeroing the card.

   4. A MutationObserver on Dawn's <cart-items> element schedules a refresh
      whenever Dawn re-renders the line items, and the custom event
      cart:refresh does the same for anything else that changes the cart.

   5. No customer-facing text in this file; everything comes from the
      section settings through [data-sg-cart-summary-config].
   ============================================================================= */

(function () {
  'use strict';

  const root = document.querySelector('[data-sg-cart-summary]');
  if (!root) return;

  /* ============================================================================
     CONFIG (from the Liquid section settings)
     ============================================================================ */
  let config = { strings: {}, urls: {}, cart: {} };
  try {
    const cfgEl = document.querySelector('[data-sg-cart-summary-config]');
    if (cfgEl) config = JSON.parse(cfgEl.textContent);
  } catch (e) { /* the seeded defaults below carry on */ }

  const S = config.strings || {};
  const U = config.urls || {};
  function str(key, fallback) {
    return (S[key] && String(S[key]).length > 0) ? S[key] : fallback;
  }
  function url(key, fallback) {
    return (U[key] && String(U[key]).length > 0) ? U[key] : fallback;
  }

  /* ============================================================================
     THE SHOPIFY CART
     Seeded from Liquid so the first render needs no network, then corrected
     by refreshCart(). Figures arrive in rappen and are held here in francs.
     ============================================================================ */
  const cartSeed = config.cart || {};

  function rappen(v) {
    const n = Number(v);
    return isFinite(n) ? n / 100 : 0;
  }

  let cartState = {
    count:    Number(cartSeed.itemCount) > 0 ? Number(cartSeed.itemCount) : 0,
    total:    rappen(cartSeed.totalPrice),
    original: rappen(cartSeed.originalTotalPrice),
    discount: rappen(cartSeed.totalDiscount),
    codes:    Array.isArray(cartSeed.discountTitles) ? cartSeed.discountTitles.filter(Boolean) : []
  };
  if (!(cartState.original > 0)) {
    cartState.original = cartState.total + cartState.discount;
  }

  /* ============================================================================
     ELEMENTS
     ============================================================================ */
  const $itemsLbl     = root.querySelector('[data-sg-purchase-items-label]');
  const $items        = root.querySelector('[data-sg-purchase-items]');
  const $discRow      = root.querySelector('[data-sg-purchase-discount-row]');
  const $discCode     = root.querySelector('[data-sg-purchase-discount-code]');
  const $discount     = root.querySelector('[data-sg-purchase-discount]');
  const $total        = root.querySelector('[data-sg-purchase-total]');

  const $continueBtn  = root.querySelector('[data-sg-continue]');
  const $continueText = root.querySelector('[data-sg-continue-text]');

  const $termsBox     = root.querySelector('[data-sg-accept-terms]');
  const $termsError   = root.querySelector('[data-sg-terms-error]');

  /* ============================================================================
     HELPERS
     ============================================================================ */
  function formatCHF(n) { return Number(n).toFixed(2); }
  function countLabel(n) {
    return n + ' ' + (n === 1 ? str('itemSingular', 'item') : str('itemPlural', 'items'));
  }

  /* Returns the live cart, or null if it could not be read. */
  async function getShopifyCart() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error('[Seconda] Failed to fetch cart:', e);
      return null;
    }
  }

  /* Cart-level codes and line-level allocations, each name once. */
  function discountTitles(cart) {
    const titles = [];
    (cart.cart_level_discount_applications || []).forEach(d => {
      if (d && d.title && titles.indexOf(d.title) === -1) titles.push(d.title);
    });
    (cart.items || []).forEach(item => {
      (item.line_level_discount_allocations || []).forEach(a => {
        const title = a && a.discount_application && a.discount_application.title;
        if (title && titles.indexOf(title) === -1) titles.push(title);
      });
    });
    return titles;
  }

  /* ============================================================================
     RENDER — SYNCHRONOUS. Do not make this async.
     ============================================================================ */
  function render() {
    const hasItems = cartState.count > 0;

    /* With nothing in the basket, Dawn shows its own empty state and this
       section steps aside entirely, including the spacing rules that key
       off sg-has-summary. */
    root.hidden = !hasItems;
    document.documentElement.classList.toggle('sg-has-summary', hasItems);
    if (!hasItems) return;

    if ($itemsLbl) $itemsLbl.textContent = countLabel(cartState.count);
    if ($items)    $items.textContent = 'CHF ' + formatCHF(cartState.original);

    const hasDiscount = cartState.discount > 0;
    if ($discRow)  $discRow.hidden = !hasDiscount;
    if ($discount) $discount.textContent = '- CHF ' + formatCHF(cartState.discount);
    if ($discCode) {
      const codes = (cartState.codes || []).join(', ');
      $discCode.hidden = !codes;
      $discCode.textContent = codes;
    }

    if ($total) $total.textContent = 'CHF ' + formatCHF(cartState.total);
    if ($continueText) $continueText.textContent = str('continueLabel', 'Continue to checkout');
  }

  /* ============================================================================
     REFRESH — the only place a fetch belongs
     ============================================================================ */
  async function refreshCart() {
    const cart = await getShopifyCart();
    if (!cart) return;

    const total    = (cart.total_price || 0) / 100;
    const discount = (cart.total_discount || 0) / 100;

    cartState = {
      count:    cart.item_count || 0,
      total:    total,
      original: cart.original_total_price != null
        ? cart.original_total_price / 100
        : total + discount,
      discount: discount,
      codes:    discountTitles(cart)
    };

    render();
  }

  /* ============================================================================
     ACTIONS
     ============================================================================ */
  if ($continueBtn) {
    $continueBtn.addEventListener('click', () => {
      if ($termsBox && !$termsBox.checked) {
        if ($termsError) $termsError.hidden = false;
        $termsBox.focus();
        return;
      }
      window.location.href = url('checkout', '/checkout');
    });
  }

  if ($termsBox) {
    $termsBox.addEventListener('change', () => {
      if ($termsBox.checked && $termsError) $termsError.hidden = true;
    });
  }

  /* ============================================================================
     INIT
     ============================================================================ */
  let refreshTimer = null;
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCart, 150);
  }

  function watchDawnCart() {
    const dawnCart = document.querySelector('cart-items')
      || document.getElementById('main-cart-items')
      || document.querySelector('.main-cart-items');
    if (!dawnCart) return;
    new MutationObserver(scheduleRefresh)
      .observe(dawnCart, { childList: true, subtree: true });
  }

  /* Safari and Firefox can serve the frozen page on Back. The cart may have
     changed in the meantime, so the figures are fetched again. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    refreshCart();
  });

  function init() {
    render();
    refreshCart();
    watchDawnCart();
    document.addEventListener('cart:refresh', refreshCart);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();